import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, type PanInfo } from 'framer-motion';
import { Lock, Sparkles, X } from 'lucide-react';

import type { CharacterAnalysisCharacter, CharacterAnalysisItem } from '../api/characterAnalysis';
import type { CharacterRelationshipItem } from '../api/characterRelationships';
import SpoilerInfoTooltip from '@/components/ui/SpoilerInfoTooltip';
import SpoilAccessSwitch from '@/features/reading/components/SpoilAccessSwitch';

interface CharacterAnalysisBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  characters: CharacterAnalysisCharacter[];
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  onRetry: () => void;
  theme?: 'white' | 'sepia' | 'dark';
  maxHeightVh?: number;
  bottomOffset?: string;
  isOnboarding?: boolean;
  disableDismiss?: boolean;
  isAnalysisSpoil: boolean;
  onAnalysisSpoilChange: (value: boolean) => void;
  isRelationshipSpoil: boolean;
  onRelationshipSpoilChange: (value: boolean) => void;
  canDisableSpoil?: boolean;
  bookId?: number;
  bookTitle?: string;
}

interface ExtendedCharacter extends CharacterAnalysisCharacter {
  relationships?: CharacterRelationshipItem[];
  character_id?: number;
}

const RING_SIZE = 92;
const RING_STROKE = 4;
const CIRCLE_RADIUS = 40;
const CIRCLE_CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS;
const CAROUSEL_ITEM_WIDTH = 96;

const toPersianDigits = (value: number | string): string =>
  String(value).replace(/[0-9]/g, (d) => ['\u06F0', '\u06F1', '\u06F2', '\u06F3', '\u06F4', '\u06F5', '\u06F6', '\u06F7', '\u06F8', '\u06F9'][parseInt(d, 10)]);

const CharacterAnalysisBottomSheet: React.FC<CharacterAnalysisBottomSheetProps> = ({
  isOpen,
  onClose,
  characters,
  isLoading,
  isError,
  errorMessage,
  onRetry,
  theme = 'white',
  maxHeightVh = 86,
  bottomOffset,
  isOnboarding = false,
  disableDismiss = false,
  isAnalysisSpoil,
  onAnalysisSpoilChange,
  isRelationshipSpoil,
  onRelationshipSpoilChange,
  canDisableSpoil = false,
  bookId,
  bookTitle,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedRelationshipId, setSelectedRelationshipId] = useState<number | null>(null);
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const selectedIndexRef = useRef(0);
  const selectionInitializedRef = useRef(false);
  const isProgrammaticScrollRef = useRef(false);
  const scrollReleaseTimerRef = useRef<number | null>(null);

  const safeCharacters = useMemo(
    () => {
      const uniqueByName = new Set<string>();
      return (Array.isArray(characters) ? characters : []).filter(
        (character): character is CharacterAnalysisCharacter =>
          !!character &&
          typeof character.character_name === 'string' &&
          (!uniqueByName.has(character.character_name) && uniqueByName.add(character.character_name)) &&
          character.character_name.trim().length > 0 &&
          typeof character.character_icon === 'string'
      );
    },
    [characters]
  ) as ExtendedCharacter[];

  const selectedCharacter = safeCharacters[selectedIndex];
  const analyses = useMemo(() => selectedCharacter?.analyses ?? [], [selectedCharacter]);
  const relationships = useMemo(() => {
    const rels = selectedCharacter?.relationships ?? [];
    const uniqueIds = new Set<number>();
    return rels.filter(r => {
      if (!r || typeof r.relationship_id !== 'number' || uniqueIds.has(r.relationship_id)) return false;
      uniqueIds.add(r.relationship_id);
      return true;
    });
  }, [selectedCharacter]);

  const lastUnlockedAnalysisIndex = useMemo(() => {
    for (let i = analyses.length - 1; i >= 0; i -= 1) {
      if (analyses[i].status === 'unlocked') return i;
    }
    return -1;
  }, [analyses]);

  const centerCharacterAtIndex = (index: number, behavior: ScrollBehavior = 'smooth') => {
    const container = carouselRef.current;
    if (!container) return;
    const target = container.querySelector<HTMLElement>(`[data-character-index="${index}"]`);
    if (target) {
      target.scrollIntoView({ behavior, block: 'nearest', inline: 'center' });
    }
  };

  useEffect(() => {
    selectedIndexRef.current = selectedIndex;
  }, [selectedIndex]);

  useEffect(() => {
    if (selectedIndex >= safeCharacters.length && safeCharacters.length > 0) {
      setSelectedIndex(safeCharacters.length - 1);
    }
  }, [selectedIndex, safeCharacters.length]);

  useEffect(() => {
    if (!isOpen) {
      selectionInitializedRef.current = false;
      return;
    }
    if (selectionInitializedRef.current || !safeCharacters.length) return;

    const bestIndex = safeCharacters.reduce(
      (best, current, index) =>
        current.progress_percentage > safeCharacters[best].progress_percentage ? index : best,
      0
    );
    setSelectedIndex(bestIndex);
    selectionInitializedRef.current = true;
    requestAnimationFrame(() => centerCharacterAtIndex(bestIndex, 'auto'));
  }, [isOpen, safeCharacters]);

  useEffect(() => {
    const container = carouselRef.current;
    if (!isOpen || !container) return;

    let scrollTimeout: number;

    const handleScroll = () => {
      if (isProgrammaticScrollRef.current) {
        window.clearTimeout(scrollTimeout);
        scrollTimeout = window.setTimeout(() => {
          isProgrammaticScrollRef.current = false;
        }, 150);
        return;
      }
      
      const scrollLeft = container.scrollLeft;
      const index = Math.round(scrollLeft / CAROUSEL_ITEM_WIDTH);
      
      if (index >= 0 && index < safeCharacters.length && index !== selectedIndexRef.current) {
        setSelectedIndex(index);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.clearTimeout(scrollTimeout);
    };
  }, [isOpen, safeCharacters]);

  useEffect(() => {
    if (!relationships.length) {
      setSelectedRelationshipId(null);
      return;
    }

    const firstUnlocked = relationships.find((r) => r.status === 'unlocked');
    const nextSelectedId =
      selectedRelationshipId && relationships.some((r) => r.relationship_id === selectedRelationshipId)
        ? selectedRelationshipId
        : (firstUnlocked?.relationship_id ?? relationships[0].relationship_id);

    setSelectedRelationshipId(nextSelectedId);
  }, [relationships, selectedRelationshipId]);

  const handleDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const shouldClose = info.offset.y > 110 || info.velocity.y > 320;
    if (shouldClose) onClose();
  };

  const sheetClasses = theme === 'dark'
    ? 'border-gray-700 bg-gray-900'
    : theme === 'sepia'
      ? 'border-[#d9c7a9] bg-[#f4ecd8]'
      : 'border-white/60 bg-[#f9f9ff]';

  const headerClasses = theme === 'dark'
    ? 'border-gray-700 bg-gray-900/95'
    : theme === 'sepia'
      ? 'border-[#d9c7a9] bg-[#f4ecd8]/95'
      : 'border-[#ecebff] bg-[#f9f9ff]/95';

  const closeBtnClasses = theme === 'dark'
    ? 'text-gray-300 hover:bg-gray-800'
    : theme === 'sepia'
      ? 'text-[#6b5333] hover:bg-[#e8dcc6]'
      : 'text-[#5d56c9] hover:bg-[#ebe9ff]';

  const isDark = theme === 'dark';
  const isSepia = theme === 'sepia';
  const titleClasses = isDark ? 'text-gray-100' : isSepia ? 'text-[#5c4a2f]' : 'text-[#2d2a57]';
  const surfaceClasses = isDark
    ? 'border-gray-700 bg-gray-800 text-white'
    : isSepia
      ? 'border-[#b99f74] bg-[#7a5a32] text-white'
      : 'border-[#ecebff] bg-white';

  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-[70] bg-black/35 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
            onClick={disableDismiss ? undefined : onClose}
          />

          <motion.div
            className="fixed inset-x-0 bottom-0 z-[71] mx-auto w-full max-w-md"
            style={{ bottom: 0 }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 28 }}
            drag={disableDismiss ? false : 'y'}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0.05, bottom: 0.55 }}
            onDragEnd={handleDragEnd}
            dir="rtl"
          >
            <div
              className={`overflow-hidden rounded-t-[28px] border shadow-2xl ${sheetClasses}`}
              style={{ 
                maxHeight: bottomOffset
                  ? `min(${maxHeightVh}vh, calc(100dvh - var(--onboarding-header-h, 110px) - 0.5rem))`
                  : `${maxHeightVh}vh`,
                paddingBottom: bottomOffset
                  ? `calc(env(safe-area-inset-bottom) + ${bottomOffset})`
                  : 'env(safe-area-inset-bottom)'
              }}
            >
              <div className={`sticky top-0 z-10 border-b ${bottomOffset ? 'px-3 pb-2 pt-1.5' : 'px-4 pb-3 pt-2'} backdrop-blur ${headerClasses}`}>
                <div className={`mx-auto mb-1.5 h-1 w-12 rounded-full ${theme === 'dark' ? 'bg-gray-600' : theme === 'sepia' ? 'bg-[#cbb797]' : 'bg-[#d5d2ff]'}`} />
                <div className="relative flex items-center justify-center">
                  {!disableDismiss && <button
                    onClick={onClose}
                    className={`absolute left-0 rounded-full p-2 transition-colors ${closeBtnClasses}`}
                    aria-label="بستن"
                  >
                    <X className="h-5 w-5" />
                  </button>}
                  <h3 className={`text-center font-persian ${bottomOffset ? 'text-[15px]' : 'text-base'} font-bold ${titleClasses}`}>شخصیت‌ها</h3>
                </div>
              </div>

              <div
                className={`overflow-y-auto ${bottomOffset ? 'px-3 pb-24 pt-2' : 'px-4 pb-10 pt-3'}`}
                style={{ maxHeight: `calc(${maxHeightVh}vh - 80px)` }}
              >
                {isLoading && (
                  <div className="space-y-3">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="h-20 animate-pulse rounded-2xl bg-[#ecebff]" />
                    ))}
                  </div>
                )}

                {!isLoading && isError && (
                  <div className="rounded-2xl border border-red-100 bg-white p-4 text-right" dir="rtl">
                    <p className="font-persian text-sm text-red-600">{errorMessage || 'خطا در بارگذاری تحلیل شخصیت‌ها رخ داد.'}</p>
                    <button
                      onClick={onRetry}
                      className="mt-3 rounded-xl bg-[#6a5be2] px-4 py-2 font-persian text-sm text-white"
                    >
                      تلاش مجدد
                    </button>
                  </div>
                )}

                {!isLoading && !isError && safeCharacters.length === 0 && (
                  <div className="rounded-2xl border border-[#ecebff] bg-white p-4 text-right" dir="rtl">
                    <p className="flex items-center justify-center gap-0.5 font-persian text-lg font-bold text-[#5f5a9a]" dir="rtl">
                      <span>به زودی</span>
                      {[0, 1, 2].map((index) => (
                        <motion.span
                          key={index}
                          animate={{ opacity: [0, 1, 0] }}
                          transition={{ duration: 1.4, repeat: Infinity, delay: index * 0.25 }}
                        >
                          .
                        </motion.span>
                      ))}
                    </p>
                  </div>
                )}

                {!isLoading && !isError && safeCharacters.length > 0 && selectedCharacter && (
                  <div className="space-y-4">
                    <div
                      ref={carouselRef}
                      className="hide-scrollbar flex snap-x snap-mandatory items-start gap-0 overflow-x-auto overflow-y-visible px-1 py-4"
                      dir="ltr"
                    >
                      <div
                        className="flex-shrink-0"
                        style={{ width: `calc(50% - ${CAROUSEL_ITEM_WIDTH / 2}px)` }}
                        aria-hidden
                      />
                      {safeCharacters.map((character, index) => {
                        const progress = Math.max(0, Math.min(100, Number(character.progress_percentage ?? 0)));
                        const strokeDashoffset = CIRCLE_CIRCUMFERENCE * (1 - progress / 100);
                        const isActive = index === selectedIndex;

                        return (
                          <button
                            key={`${character.character_name}-${index}`}
                            data-character-index={index}
                            onClick={() => {
                              setSelectedIndex(index);
                              isProgrammaticScrollRef.current = true;
                              centerCharacterAtIndex(index, 'smooth');
                              
                              if (scrollReleaseTimerRef.current) window.clearTimeout(scrollReleaseTimerRef.current);
                              scrollReleaseTimerRef.current = window.setTimeout(() => {
                                isProgrammaticScrollRef.current = false;
                              }, 500);
                            }}
                            className={`relative flex w-[96px] snap-center flex-shrink-0 flex-col items-center transition-all duration-300 ${
                              isActive ? 'z-20 -translate-y-2 scale-110 opacity-100' : 'z-0 scale-[0.82] opacity-45'
                            }`}
                            dir="rtl"
                          >
                            <div className="relative" style={{ height: `${RING_SIZE}px`, width: `${RING_SIZE}px` }}>
                              <svg className="absolute inset-0 -rotate-90" viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}>
                                <circle
                                  cx={RING_SIZE / 2}
                                  cy={RING_SIZE / 2}
                                  r={CIRCLE_RADIUS}
                                  fill="none"
                                  stroke={isDark ? '#45416f' : isSepia ? '#c79b67' : '#e7e4ff'}
                                  strokeWidth={RING_STROKE}
                                />
                                <circle
                                  cx={RING_SIZE / 2}
                                  cy={RING_SIZE / 2}
                                  r={CIRCLE_RADIUS}
                                  fill="none"
                                  stroke={isDark ? '#6f63ff' : isSepia ? '#8a5d28' : '#6f63ff'}
                                  strokeWidth={RING_STROKE}
                                  strokeLinecap="round"
                                  strokeDasharray={CIRCLE_CIRCUMFERENCE}
                                  strokeDashoffset={strokeDashoffset}
                                  style={{ transition: 'stroke-dashoffset 0.3s ease' }}
                                />
                              </svg>
                              <div className="absolute inset-[10px] overflow-hidden rounded-full border-2 border-white shadow-sm">
                                <img
                                  src={character.character_icon}
                                  alt={character.character_name}
                                  className="h-full w-full object-cover"
                                  onError={(e) => {
                                    (e.currentTarget as HTMLImageElement).src =
                                      'https://api.dicebear.com/7.x/thumbs/svg?seed=' + encodeURIComponent(character.character_name);
                                  }}
                                />
                              </div>
                            </div>
                            <p className={`mt-2 line-clamp-2 min-h-9 text-center font-persian text-xs font-semibold ${isDark ? 'text-white' : isSepia ? 'text-white' : 'text-[#2f2b63]'}`}>
                              {character.character_name}
                            </p>
                            {isActive && (
                              <span className={`mt-0.5 whitespace-nowrap rounded-full px-2.5 py-1 font-persian text-[9px] font-semibold leading-none text-white ${
                                isDark ? 'bg-[#4f63ff]' : isSepia ? 'bg-[#8a5d28]' : 'bg-[#6f63ff]'
                              }`}>
                                پیشرفت: {toPersianDigits(Math.round(progress))}٪
                              </span>
                            )}
                          </button>
                        );
                      })}
                      <div
                        className="flex-shrink-0"
                        style={{ width: `calc(50% - ${CAROUSEL_ITEM_WIDTH / 2}px)` }}
                        aria-hidden
                      />
                     </div>

                    {relationships.length > 0 && (
                      <div className={`rounded-[30px] border px-4 py-5 shadow-[0_14px_36px_rgba(111,99,255,0.08)] ${surfaceClasses}`}>
                        <div className="mb-4 flex items-center justify-between gap-3">
                          <p className={`font-persian text-sm font-bold ${isDark ? 'text-white' : isSepia ? 'text-white' : 'text-[#2f2b63]'}`}>روابط</p>
                          <div className="flex items-center gap-2" dir="rtl">
                            <div className="relative">
                              <span className={`font-persian text-xs font-semibold ${isSepia ? 'text-white' : titleClasses}`}>لو نده</span>
                              <SpoilerInfoTooltip
                                description="روشن بودن یعنی نمایش روابط شخصیت بر اساس قسمت هایی هست که خوندی.با خاموش کردن نمایش روابط شخصیت بر اساس کل قسمت ها هست. حتی اون قسمت هایی که نخوندی"
                                className="absolute -top-3 -right-1"
                              />
                            </div>
                            <SpoilAccessSwitch
                              label=""
                              isSpoil={isRelationshipSpoil}
                              canDisableSpoil={canDisableSpoil}
                              onSpoilChange={onRelationshipSpoilChange}
                              theme={theme}
                              bookId={bookId}
                              bookTitle={bookTitle}
                              subscriptionButtonType="character_relationships"
                            />
                          </div>
                        </div>

                        <div className={`mb-6 rounded-[24px] border p-3 shadow-[inset_0_2px_12px_rgba(111,99,255,0.03)] ${isDark ? 'border-gray-700 bg-gray-900' : isSepia ? 'border-[#b99f74] bg-[#6e4f2d]' : 'border-[#ecebff] bg-[#faf9ff]'}`} dir="ltr">
                          <div className="relative flex items-center justify-between gap-0" dir="rtl">
                            {/* Related Character (Changes - now on Right in UI, but 1st child in RTL flex) */}
                            <div className="z-10 flex w-[80px] flex-shrink-0 flex-col items-center">
                              {(() => {
                                const rel = relationships.find((r) => r.relationship_id === selectedRelationshipId);
                                const isLocked = !rel || rel.status !== 'unlocked';

                                return (
                                  <AnimatePresence mode="popLayout">
                                    <motion.div
                                      key={rel?.relationship_id || 'empty'}
                                      className={`flex h-[72px] w-[72px] items-center justify-center rounded-full border-[3px] border-white shadow-[0_12px_28px_rgba(33,28,90,0.08)] ${isLocked ? 'bg-[#efedff]' : 'bg-white'}`}
                                      initial={{ scale: 0.8, opacity: 0, x: 20 }}
                                      animate={{ scale: 1, opacity: 1, x: 0 }}
                                      exit={{ scale: 0.8, opacity: 0, x: -20 }}
                                      transition={{ type: 'spring', bounce: 0.4 }}
                                    >
                                      {isLocked ? (
                                        <Lock className={`h-6 w-6 ${isSepia ? 'text-[#8a5d28]' : 'text-[#9a91ed]'}`} />
                                      ) : (
                                        <div className="h-[60px] w-[60px] overflow-hidden rounded-full">
                                          <img
                                            src={rel?.related_character_icon || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(rel?.related_character_name || '')}`}
                                            alt={rel?.related_character_name}
                                            className="h-full w-full object-cover"
                                            onError={(e) => {
                                              if (rel) {
                                                (e.currentTarget as HTMLImageElement).src = `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(rel.related_character_name)}`;
                                              }
                                            }}
                                          />
                                        </div>
                                      )}
                                    </motion.div>
                                  </AnimatePresence>
                                );
                              })()}
                              <div className={`mt-2.5 rounded-full border px-2.5 py-1 shadow-sm backdrop-blur-sm ${isDark ? 'border-gray-600 bg-gray-800/90' : isSepia ? 'border-[#b99f74] bg-[#7a5a32]/90' : 'border-[#ecebff] bg-white/90'}`}>
                                <p className={`max-w-[64px] truncate text-center font-persian text-[9px] font-bold ${isDark ? 'text-white' : isSepia ? 'text-white' : 'text-[#2f2b63]'} ${relationships.find((r) => r.relationship_id === selectedRelationshipId)?.status !== 'unlocked' ? 'blur-[4px] select-none' : ''}`} dir="rtl">
                                  {(() => {
                                    const rel = relationships.find((r) => r.relationship_id === selectedRelationshipId);
                                    if (!rel) return '...';
                                    return rel.related_character_name;
                                  })()}
                                </p>
                              </div>
                            </div>

                            {/* Connection Link */}
                            <div className="relative z-0 flex h-10 flex-1 items-center justify-center" dir="ltr">
                              <div className={`absolute h-[2px] w-full bg-gradient-to-r from-transparent ${isDark || isSepia ? 'via-white' : 'via-[#6f63ff]'} to-transparent opacity-40`} />
                              <motion.div
                                className={`absolute h-[7px] w-[7px] rounded-full ${isDark || isSepia ? 'bg-white shadow-[0_0_10px_#ffffff]' : 'bg-[#6f63ff] shadow-[0_0_10px_#6f63ff]'}`}
                                animate={{ left: ['90%', '10%'] }}
                                transition={{ duration: 1.6, repeat: Number.POSITIVE_INFINITY, ease: 'easeInOut' }}
                              />
                              <div className={`relative z-10 flex items-center justify-center rounded-full border px-2.5 py-1.5 shadow-[0_8px_16px_rgba(111,99,255,0.06)] ${isDark ? 'border-gray-600 bg-gray-800' : isSepia ? 'border-[#b99f74] bg-[#7a5a32]' : 'border-[#ecebff] bg-white'}`}>
                                <p className={`whitespace-nowrap font-persian text-[9px] font-extrabold ${isDark || isSepia ? 'text-white' : 'text-[#6f63ff]'} ${relationships.find((r) => r.relationship_id === selectedRelationshipId)?.status !== 'unlocked' ? 'blur-[4px] select-none' : ''}`} dir="rtl">
                                  {(() => {
                                    const rel = relationships.find((r) => r.relationship_id === selectedRelationshipId);
                                    if (!rel) return 'رابطه‌ای انتخاب نشده';
                                    return rel.relationship_type;
                                  })()}
                                </p>
                              </div>
                            </div>

                            {/* Main Character (Fixed - now on Left in UI, but 3rd child in RTL flex) */}
                            <div className="z-10 flex w-[80px] flex-shrink-0 flex-col items-center">
                              <motion.div
                                className={`flex h-[72px] w-[72px] items-center justify-center rounded-full border-[3px] ${
                                  isSepia
                                    ? 'border-[#8a5d28] bg-[#8a5d28] shadow-[0_12px_28px_rgba(138,93,40,0.35)]'
                                    : 'border-white bg-[#6f63ff] shadow-[0_12px_28px_rgba(111,99,255,0.2)]'
                                }`}
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ type: 'spring', bounce: 0.4 }}
                              >
                                <div className="h-[60px] w-[60px] overflow-hidden rounded-full border border-white/40">
                                  <img
                                    src={selectedCharacter.character_icon || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(selectedCharacter.character_name)}`}
                                    alt={selectedCharacter.character_name}
                                    className="h-full w-full object-cover"
                                    onError={(e) => {
                                      (e.currentTarget as HTMLImageElement).src = `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(selectedCharacter.character_name)}`;
                                    }}
                                  />
                                </div>
                              </motion.div>
                              <div className={`mt-2.5 rounded-full border px-2.5 py-1 shadow-sm backdrop-blur-sm ${isDark ? 'border-gray-600 bg-gray-800/90' : isSepia ? 'border-[#b99f74] bg-[#7a5a32]/90' : 'border-[#ecebff] bg-white/90'}`}>
                                <p className={`max-w-[64px] truncate text-center font-persian text-[9px] font-bold ${isDark ? 'text-white' : isSepia ? 'text-white' : 'text-[#2f2b63]'}`} dir="rtl">
                                  {selectedCharacter.character_name}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="hide-scrollbar flex snap-x snap-mandatory gap-3 overflow-x-auto pb-3 pt-1" dir="rtl">
                          {relationships.map((relationship) => {
                            const isLocked = relationship.status !== 'unlocked';
                            const isSelected = relationship.relationship_id === selectedRelationshipId;

                            return (
                              <motion.button
                                key={relationship.relationship_id}
                                type="button"
                                onClick={() => setSelectedRelationshipId(relationship.relationship_id)}
                                className={`flex shrink-0 snap-center items-center gap-2 rounded-full border p-1.5 pr-2 transition-all duration-200 ${
                                  isSelected
                                    ? (isDark ? 'border-[#7f8cff] bg-[#2f3f9a] shadow-[0_6px_16px_rgba(70,90,255,0.22)]' : isSepia ? 'border-[#d6a566] bg-[#8a5d28] shadow-[0_6px_16px_rgba(185,135,65,0.24)]' : 'border-[#6f63ff] bg-[#f5f4ff] shadow-[0_6px_16px_rgba(111,99,255,0.12)]')
                                    : (isDark ? 'border-gray-700 bg-gray-800 opacity-90 hover:opacity-100' : isSepia ? 'border-[#b99f74] bg-[#7a5a32] opacity-90 hover:opacity-100' : 'border-[#ecebff] bg-white opacity-80 hover:opacity-100')
                                }`}
                              >
                                <div className={`flex h-8 w-8 items-center justify-center overflow-hidden rounded-full ${isLocked ? 'bg-[#efedff]' : 'bg-[#f0eeff]'}`}>
                                  {isLocked ? (
                                    <Lock className={`h-3 w-3 ${isSepia ? 'text-[#8a5d28]' : 'text-[#9a91ed]'}`} />
                                  ) : (
                                    <img
                                      src={relationship.related_character_icon || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(relationship.related_character_name)}`}
                                      alt={relationship.related_character_name}
                                      className="h-full w-full object-cover"
                                    />
                                  )}
                                </div>
                                <span className={`pl-2 font-persian text-[10px] font-bold ${
                                  isDark || isSepia
                                    ? 'text-white'
                                    : (isSelected ? 'text-[#6f63ff]' : 'text-[#5b56a1]')
                                } ${isLocked ? 'blur-[3px] select-none' : ''} transition-[filter] duration-300`}>
                                  {relationship.related_character_name}
                                </span>
                              </motion.button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className={`rounded-[30px] border px-4 py-5 shadow-[0_14px_36px_rgba(111,99,255,0.08)] ${surfaceClasses} ${isOnboarding ? 'mb-16' : ''}`}>
                      <div className="mb-4 flex items-center justify-between gap-3">
                        <p className={`font-persian text-sm font-bold ${isDark ? 'text-white' : isSepia ? 'text-white' : 'text-[#2f2b63]'}`}>تحلیل شخصیت</p>
                        <div className="flex items-center gap-2" dir="rtl">
                          <div className="relative">
                            <span className={`font-persian text-xs font-semibold ${isSepia ? 'text-white' : titleClasses}`}>لو نده</span>
                            <SpoilerInfoTooltip
                              description="روشن بودن یعنی نمایش تحلیل شخصیت بر اساس قسمت هایی هست که خوندی.با خاموش کردن نمایش تحلیل شخصیت بر اساس کل قسمت ها هست. حتی اون قسمت هایی که نخوندی"
                              className="absolute -top-3 -right-1"
                            />
                          </div>
                          <SpoilAccessSwitch
                            label=""
                            isSpoil={isAnalysisSpoil}
                            canDisableSpoil={canDisableSpoil}
                            onSpoilChange={onAnalysisSpoilChange}
                            theme={theme}
                            bookId={bookId}
                            bookTitle={bookTitle}
                            subscriptionButtonType="character_analysis"
                          />
                        </div>
                      </div>
                      <div className="-mx-2 space-y-3 pb-2">
                        {analyses.length === 0 && (
                          <div className="rounded-2xl border border-[#ecebff] bg-white px-4 py-3 text-right" dir="rtl">
                            <p className="font-persian text-xs text-[#7773a5]">هنوز تحلیلی برای این شخصیت ثبت نشده است.</p>
                          </div>
                        )}

                        {analyses.map((analysis, index) => {
                          const isLeft = index % 2 === 0;
                          const isLocked = analysis.status !== 'unlocked';
                          const isReached = index <= lastUnlockedAnalysisIndex;

                          return (
                            <motion.div
                              key={`${analysis.section_number}-${index}`}
                              initial={{ opacity: 0, y: 6 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.2, delay: index * 0.03 }}
                              className={`relative flex items-center px-0 ${isLeft ? 'justify-start' : 'justify-end'} gap-2`}
                              dir="rtl"
                            >
                              {isLeft ? (
                                <>
                                  <div
                                    className={`relative min-h-[64px] w-full max-w-[316px] rounded-[32px] border px-5 py-3 text-right ${
                                      isLocked ? 'border-[#e9e7ff] bg-[#f3f2ff]' : 'border-[#ded9ff] bg-white'
                                    }`}
                                  >
                                    {isLocked && (
                                      <span className={`absolute inset-0 flex items-center justify-center ${isSepia ? 'text-[#8a5d28]/90' : 'text-[#6f63ff]/90'}`}>
                                        <Lock className="h-5 w-5" />
                                      </span>
                                    )}
                                    <p
                                      className={`font-persian text-[11px] leading-5 text-[#4a4588] transition-[filter] duration-300 ${isLocked ? 'select-none blur-[3px]' : ''}`}
                                    >
                                      {analysis.analysis_text || '...'}
                                    </p>
                                  </div>
                                  <span
                                    className={`z-20 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border-2 shadow-sm ${
                                      isReached
                                        ? (isDark ? 'border-[#4f63ff] bg-[#4f63ff] text-white' : isSepia ? 'border-[#5a371c] bg-[#6b4423] text-white' : 'border-[#6f63ff] bg-[#6f63ff] text-white')
                                        : (isDark ? 'border-gray-600 bg-gray-800 text-white' : isSepia ? 'border-[#b99f74] bg-[#ead9bd] text-[#6b4423]' : 'border-[#cfc9ff] bg-white text-[#635acc]')
                                    }`}
                                  >
                                    <span className="font-persian text-sm font-bold">{toPersianDigits(analysis.section_number)}</span>
                                  </span>
                                </>
                              ) : (
                                <>
                                  <span
                                    className={`z-20 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border-2 shadow-sm ${
                                      isReached
                                        ? (isDark ? 'border-[#4f63ff] bg-[#4f63ff] text-white' : isSepia ? 'border-[#5a371c] bg-[#6b4423] text-white' : 'border-[#6f63ff] bg-[#6f63ff] text-white')
                                        : (isDark ? 'border-gray-600 bg-gray-800 text-white' : isSepia ? 'border-[#b99f74] bg-[#ead9bd] text-[#6b4423]' : 'border-[#cfc9ff] bg-white text-[#635acc]')
                                    }`}
                                  >
                                    <span className="font-persian text-sm font-bold">{toPersianDigits(analysis.section_number)}</span>
                                  </span>
                                  <div
                                    className={`relative min-h-[64px] w-full max-w-[316px] rounded-[32px] border px-5 py-3 text-right ${
                                      isLocked ? 'border-[#e9e7ff] bg-[#f3f2ff]' : 'border-[#ded9ff] bg-white'
                                    }`}
                                  >
                                    {isLocked && (
                                      <span className={`absolute inset-0 flex items-center justify-center ${isSepia ? 'text-[#8a5d28]/90' : 'text-[#6f63ff]/90'}`}>
                                        <Lock className="h-5 w-5" />
                                      </span>
                                    )}
                                    <p
                                      className={`font-persian text-[11px] leading-5 text-[#4a4588] transition-[filter] duration-300 ${isLocked ? 'select-none blur-[3px]' : ''}`}
                                    >
                                      {analysis.analysis_text || '...'}
                                    </p>
                                  </div>
                                </>
                              )}
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CharacterAnalysisBottomSheet;
